import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import { Timeline, Close, Edit, Delete } from '@mui/icons-material';
import { COLORS } from '../../shared/colors';
import OverflowTooltip from '../../shared/OverflowTooltip';

const ViewLinkedTicketsModal = ({ 
  open, 
  onClose, 
  task, 
  linkedTickets = {},
  onEditLink,
  onDeleteLink
}) => {
  const { t } = useTranslation();
  const [deletingLinkId, setDeletingLinkId] = useState(null);
  const [editingLinkId, setEditingLinkId] = useState(null);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        background: `linear-gradient(135deg, ${COLORS.gradient.primary} 0%, ${COLORS.gradient.secondary} 100%)`,
        color: 'white',
        p: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Timeline sx={{ color: 'white', fontSize: 24 }} />
            <Typography variant="h6" fontWeight="700" sx={{ color: 'white' }}>
              {t('links.viewModal.title')}
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Primary Ticket Display */}
        <Box sx={{ mb: 3, p: 2, bgcolor: COLORS.gray[50], borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {t('links.viewModal.showingLinksFor')}
          </Typography>
          <OverflowTooltip 
            title={`${task?.code} · ${task?.title}`}
            tooltipWidth={600}
            variant="body1"
            fontWeight="600"
            maxLines={2}
            placement="top-start"
            sx={{ 
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              lineHeight: 1.4
            }}
          />
        </Box>

        {/* Linked Tickets List */}
        <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: COLORS.gray[800] }}>
          {t('links.viewModal.linkedTickets')}
        </Typography>
        
        <Box>
          {linkedTickets && Object.keys(linkedTickets).length > 0 ? (
            Object.entries(linkedTickets).map(([relationshipType, relationshipData]) => (
              <Box key={relationshipType} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1, color: COLORS.gray[700] }}>
                  {relationshipType}
                </Typography>
                {(relationshipData?.tickets || []).map((ticket) => (
                  <Box key={ticket.id} sx={{ 
                    p: 2, 
                    border: `1px solid ${COLORS.gray[200]}`, 
                    borderRadius: 1, 
                    mb: 1,
                    ml: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: COLORS.gray[50],
                      borderColor: COLORS.primary
                    }
                  }}
                  onClick={() => {
                    if (ticket?.ticket_number) {
                      window.open(`/task/${encodeURIComponent(ticket.ticket_number)}`, '_blank');
                    }
                  }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <OverflowTooltip 
                        title={`${ticket?.ticket_number} · ${ticket?.title}`}
                        tooltipWidth={600}
                        variant="body2" 
                        fontWeight="600"
                        placement="top-start"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton 
                        size="small" 
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!onEditLink || editingLinkId === ticket?.relationship_id) return;
                          setEditingLinkId(ticket?.relationship_id);
                          try {
                            await onEditLink({ relationshipType, ticket, relationshipId: ticket?.relationship_id });
                          } finally {
                            setEditingLinkId(null);
                          }
                        }}
                        disabled={editingLinkId === ticket?.relationship_id || deletingLinkId === ticket?.relationship_id}
                        sx={{ color: COLORS.gray[500] }}
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!onDeleteLink || deletingLinkId === ticket?.relationship_id) return;
                          setDeletingLinkId(ticket?.relationship_id);
                          try {
                            await onDeleteLink({ relationshipType, ticket, relationshipId: ticket?.relationship_id });
                          } finally {
                            setDeletingLinkId(null);
                          }
                        }}
                        disabled={deletingLinkId === ticket?.relationship_id || editingLinkId === ticket?.relationship_id}
                        sx={{ color: COLORS.error }}
                      >
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
              {t('links.viewModal.noLinkedTicketsFound')}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {t('links.viewModal.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewLinkedTicketsModal;